import TagManager from 'react-gtm-module';

// Add type definition for window.dataLayer and plausible
declare global {
  interface Window {
    dataLayer: any[];
    plausible: (event: string, options?: { props?: Record<string, any> }) => void;
  }
}

/**
 * Google Tag Manager configuration
 */
const GTM_ID = 'GTM-5QK43N2J';

/**
 * Check if meta tags are currently being set
 * @returns {boolean} Whether meta tags are currently being set
 */
const isSettingMetaTags = (): boolean => {
  return window.document.documentElement.hasAttribute('data-setting-meta-tags');
};

/**
 * Check if a meta tag is protected
 * @param {string} name - The name or property of the meta tag
 * @returns {boolean} Whether the meta tag is protected
 */
const isMetaTagProtected = (name: string): boolean => {
  const isOgOrTwitter = name.startsWith('og:') || name.startsWith('twitter:');
  const selector = isOgOrTwitter 
    ? `meta[property="${name}"]` 
    : `meta[name="${name}"]`;
  
  const metaTag = document.querySelector(selector);
  return metaTag ? metaTag.hasAttribute('data-protected') : false;
};

/**
 * Initialize Google Tag Manager
 * This should be called as early as possible in your application lifecycle
 * BUT after meta tags have been properly set
 */
export const initGTM = (): void => {
  if (typeof window !== 'undefined') {
    try {
      // Check if meta tags are being set
      if (isSettingMetaTags()) {
        console.warn('Meta tags are being set, delaying GTM initialization');
        setTimeout(() => initGTM(), 500);
        return;
      }
      
      // Initialize dataLayer
      window.dataLayer = window.dataLayer || [];
      
      // Push initial dataLayer configuration
      window.dataLayer.push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js',
        // Important: tell GTM not to mess with meta tags
        'metaTagsPreserved': true,
        'config': {
          'respectMetaTags': true
        }
      });

      // Initialize GTM with specific configuration to avoid meta tag interference
      TagManager.initialize({
        gtmId: GTM_ID,
        // Important: Specify to not touch document meta tags when GTM initializes
        dataLayerName: 'dataLayer',
        // The preview flag tells GTM to avoid modifying the page
        preview: false,
        // This option prevents GTM from automatically sending page view events
        // which can sometimes reset meta tags
        events: {},
        // Add additional configuration to prevent meta tag modification
        dataLayer: {
          'respectMetaTags': true,
          'metaTagsPreserved': true
        }
      });

      // Add a mutation observer to protect meta tags from being modified by GTM
      const observer = new MutationObserver((mutations) => {
        // If meta tags are being set, don't interfere
        if (isSettingMetaTags()) return;
        
        for (const mutation of mutations) {
          if (mutation.type === 'attributes' && 
              mutation.target instanceof HTMLMetaElement) {
            const metaTag = mutation.target;
            const name = metaTag.getAttribute('property') || metaTag.getAttribute('name');
            
            // If this is a protected meta tag and it was modified by something other than our code
            if (name && isMetaTagProtected(name) && !isSettingMetaTags()) {
              console.warn(`Protected meta tag ${name} was modified by an external script. Restoring...`);
              
              // We could restore the original value here if needed
              // For now, just log the warning
            }
          }
        }
      });
      
      // Observe changes to meta tags
      observer.observe(document.head, { 
        childList: true, 
        attributes: true, 
        subtree: true,
        attributeFilter: ['content'] 
      });

      console.log('GTM initialized successfully with ID:', GTM_ID);
    } catch (error) {
      console.error('Failed to initialize GTM:', error);
    }
  }
};

/**
 * Track page view in Google Tag Manager for Plausible
 * Call this function whenever a route change occurs
 * @param path - Current page path
 * @param title - Page title
 */
export const trackPageView = (path: string, title: string): void => {
  if (typeof window !== 'undefined') {
    try {
      // Don't track page views while meta tags are being set
      if (isSettingMetaTags()) {
        console.warn('Meta tags are being set, delaying page view tracking');
        setTimeout(() => trackPageView(path, title), 200);
        return;
      }
      
      // Ensure dataLayer exists
      window.dataLayer = window.dataLayer || [];
      
      // Create a unique pageview event with timestamp to avoid duplicates
      const timestamp = new Date().getTime();
      
      // Push page view event for Plausible - DO NOT touch meta tags
      window.dataLayer.push({
        event: 'pageview',
        eventId: `pageview_${timestamp}`,
        page: {
          url: window.location.href,
          path: path,
          referrer: document.referrer,
          title: title,
          timestamp: timestamp
        },
        // Flag to indicate not to update meta tags
        preserveMetaTags: true
      });

      console.log('Plausible page view tracked:', {
        url: window.location.href,
        path: path,
        referrer: document.referrer,
        title: title
      });
    } catch (error) {
      console.error('Error tracking page view:', error);
    }
  }
};

/**
 * Track custom event in Google Tag Manager for Plausible
 */
export const trackEvent = (eventName: string, eventParams: Record<string, any> = {}): void => {
  if (typeof window !== 'undefined') {
    try {
      // Don't track events while meta tags are being set
      if (isSettingMetaTags()) {
        console.warn('Meta tags are being set, delaying event tracking');
        setTimeout(() => trackEvent(eventName, eventParams), 200);
        return;
      }
      
      window.dataLayer = window.dataLayer || [];
      
      // Push custom event to dataLayer in Plausible format
      const customEvent = {
        event: eventName,
        name: eventName,
        props: eventParams,
        // Flag to indicate not to update meta tags
        preserveMetaTags: true
      };
      
      window.dataLayer.push(customEvent);
      console.log(`Tracked Plausible event ${eventName}:`, customEvent);
    } catch (error) {
      console.error(`Error tracking ${eventName} event:`, error);
    }
  }
};

/**
 * Track sitemap generation event
 * @param {number} totalUrls - Total number of URLs in the sitemap
 * @param {Record<string, number>} urlCounts - Count of URLs by type
 */
export const trackSitemapGeneration = (totalUrls: number, urlCounts: Record<string, number>): void => {
  trackEvent('sitemap_generation', {
    total_urls: totalUrls,
    url_counts: urlCounts,
  });
};

/**
 * Track sitemap submission event
 * @param {boolean} success - Whether the submission was successful
 * @param {string} service - The service the sitemap was submitted to (e.g., 'google')
 */
export const trackSitemapSubmission = (success: boolean, service: string): void => {
  trackEvent('sitemap_submission', {
    success,
    service,
  });
};
